import React, { useContext, useEffect } from 'react';

import { MDText } from 'i18n-react';

import { ConfigContext } from 'Context/config';

import { getBrands } from 'Services/api';

import { Content } from 'LegacySBCRA/Content/Content';

import type { Brand } from '../../../../../client/index.helper';
import Breadcrumbs from '../../fastlane/breadcrumbs/Breadcrumbs';
import { Makes } from '../../makes/Makes';

import LocalTexts from './BrandsTexts.json';

import styles from './Brands.scss';

const LocalT = new MDText(LocalTexts);

const listItemsArray = [
  { id: 1, text: LocalT.translate('section1.item1') },
  { id: 2, text: LocalT.translate('section1.item2') },
  { id: 3, text: LocalT.translate('section1.item3') },
  { id: 4, text: LocalT.translate('section1.item4') },
  { id: 5, text: LocalT.translate('section1.item5') },
  { id: 6, text: LocalT.translate('section1.item6') },
];

export const sortBrands = (brands: Brand[]) => (Array.isArray(brands) ? brands : [])
  .sort((a, b) => a.slug.localeCompare(b.slug));

export const Brands = () => {
  const { configActions: { update }, configState: { brandData } } = useContext(ConfigContext);

  const makes = sortBrands(brandData?.brands || []);

  useEffect(() => {
    if (makes.length === 0) {
      getBrands().then((brands) => {
        update({ brandData: { brands } });
      }).catch((err) => {
        window?.Sentry?.captureException(new Error(err));
      });
    }
  }, [makes, update]);

  return (
    <Content className={styles.view}>
      <Breadcrumbs leftAlign className={styles.topNav} />
      <section style={{ minHeight: '100vh' }}>
        <h1>{LocalT.translate('title')}</h1>

        <p>
          {LocalT.translate('intro.p1', {
            link: (
              <a className="mw-link mw-link-blue" href="/sell-my-car">{LocalT.translate('intro.link1')}</a>
            ),
          })}
        </p>
        <p>{LocalT.translate('intro.p2')}</p>
        <h3>{LocalT.translate('title_major')}</h3>
        <p>{LocalT.translate('intro.p3')}</p>

        <Makes makes={makes.filter((m) => m.major === 2)} />
        <h3>{LocalT.translate('title_all')}</h3>

        <Makes makes={makes.filter((m) => m.major === 1)} />

        <h3>{LocalT.translate('section1.title')}</h3>
        <p>{LocalT.translate('section1.p1')}</p>
        <p>{LocalT.translate('section1.p2')}</p>
        <ul className={styles.listItems}>
          {
            listItemsArray.map(({ id, text }) => (
              <li key={id}>{text}</li>
            ))
          }
        </ul>
        <p>{LocalT.translate('section1.p3')}</p>

        <h3>{LocalT.translate('section2.title')}</h3>
        <p>
          {LocalT.translate('section2.p1', {
            link: (
              <a className="mw-link mw-link-blue" href="/sell-my-car/car-valuation">{LocalT.translate('section2.link1')}</a>
            ),
          })}
        </p>
        <p>{LocalT.translate('section2.p2')}</p>

        <h3>{LocalT.translate('section3.title')}</h3>
        <p>{LocalT.translate('section3.p1')}</p>
        <p>{LocalT.translate('section3.p2')}</p>

        <h3>{LocalT.translate('section4.title')}</h3>
        <p>{LocalT.translate('section4.p1')}</p>

        <h3>{LocalT.translate('section5.title')}</h3>
        <p>{LocalT.translate('section5.p1')}</p>

        <h3>{LocalT.translate('section6.title')}</h3>
        <p>
          {LocalT.translate('section6.p1', {
            link: (
              <a
                className="mw-link mw-link-blue"
                href="/sell-my-car/guides/wash-wax-keep-your-car-new"
              >
                {LocalT.translate('section6.link1')}
              </a>
            ),
          })}
        </p>
        <p>
          {LocalT.translate('section6.p2', {
            link: (
              <a
                className="mw-link mw-link-blue"
                href="/sell-my-car/on-finance"
              >
                {LocalT.translate('section6.link2')}
              </a>
            ),
          })}
        </p>

        <h3>{LocalT.translate('section7.title')}</h3>
        <p>
          {LocalT.translate('section7.p1', {
            link: (
              <a
                className="mw-link mw-link-blue"
                href="/sell-my-car/guides/car-depreciation-guide"
              >
                {LocalT.translate('section7.link1')}
              </a>
            ),
          })}
        </p>
        <p>{LocalT.translate('section7.p2')}</p>
        <p>
          {LocalT.translate('section7.p3', {
            cazLink: (
              <a
                className="mw-link mw-link-blue"
                href="/sell-my-car/guides/birmingham-clean-air-zone"
              >
                {LocalT.translate('section7.link3')}
              </a>
            ),
            ecLink: (
              <a
                className="mw-link mw-link-blue"
                href="/sell-my-car/guides/electric-cars-guide"
              >
                {LocalT.translate('section7.link5')}
              </a>
            ),
            soLink: (
              <a
                className="mw-link mw-link-blue"
                href="/sell-my-car/guides/2030-electric-car-switchover"
              >
                {LocalT.translate('section7.link4')}
              </a>
            ),
            ulezLink: (
              <a
                className="mw-link mw-link-blue"
                href="/sell-my-car/guides/ulez-ultimate-guide"
              >
                {LocalT.translate('section7.link2')}
              </a>
            ),
          })}
        </p>

        <h3>{LocalT.translate('section8.title')}</h3>
        <p>
          {LocalT.translate('section8.p1', {
            link: (
              <a
                className="mw-link mw-link-blue"
                href="/sell-my-car/guides/how-to-sell-a-car-privately"
              >
                {LocalT.translate('section8.link1')}
              </a>
            ),
          })}
        </p>
        <p>{LocalT.translate('section8.p2')}</p>
      </section>
    </Content>
  );
};

export default Brands;
